import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(customParseFormat)

/** Wire format for every date sent to / received from the API. */
export const API_DATE_FORMAT = 'YYYY-MM-DD'
/** Display formats — change here, not in screens. */
export const DISPLAY_DATE_FORMAT = 'DD MMM YYYY'
export const DISPLAY_DATE_TIME_FORMAT = 'DD MMM YYYY HH:mm'
/**
 * The longer, 12-hour stamp the Website Content grid uses
 * (`Sep 02, 2026 · 10:18 AM`). A second named format rather than a change to
 * the one above, which would re-spell every timestamp in the portal.
 */
export const DISPLAY_DATE_TIME_MEDIUM_FORMAT = 'MMM DD, YYYY · hh:mm A'
/** Date-range parts, per the events listing design: `Sep 15 – Sep 17, 2026`. */
export const RANGE_DATE_FORMAT = 'MMM DD'
export const RANGE_DATE_WITH_YEAR_FORMAT = 'MMM DD, YYYY'

/**
 * A night as the room-inventory tables label it: `Sat, Jun 14`. Nightly grids
 * are read down a column, where the weekday is what a reader scans for, and the
 * year is already stated by the record's date range.
 */
export const NIGHT_DATE_FORMAT = 'ddd, MMM DD'

/** A calendar day as the Dashboard writes it: `Oct 23, 2026` / `Nov 2, 2026`. */
export const CALENDAR_DATE_FORMAT = 'MMM D, YYYY'

export type DateInput = string | number | Date | null | undefined

/**
 * An API calendar date (`YYYY-MM-DD`) as `Oct 23, 2026`.
 *
 * Parsed strictly as a LOCAL calendar day — `new Date('2026-10-23')` would read
 * it as UTC midnight and show Oct 22 west of Greenwich. A value in any other
 * shape falls back rather than being guessed at.
 */
export function formatCalendarDate(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value, API_DATE_FORMAT, true)
  return d.isValid() ? d.format(CALENDAR_DATE_FORMAT) : fallback
}

export function formatDate(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(DISPLAY_DATE_FORMAT) : fallback
}

/** One night of inventory: `Sat, Jun 14`. */
export function formatNightDate(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(NIGHT_DATE_FORMAT) : fallback
}

/**
 * One night with its year: `Mon, Sep 15, 2026` — the reservation Stay tab's
 * inventory and nightly-rate rows, which the 2026-10 design dates in full.
 */
export const NIGHT_DATE_WITH_YEAR_FORMAT = 'ddd, MMM DD, YYYY'

export function formatNightDateWithYear(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(NIGHT_DATE_WITH_YEAR_FORMAT) : fallback
}

export function formatDateTime(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(DISPLAY_DATE_TIME_FORMAT) : fallback
}

/**
 * A plain two-decimal amount with no currency — a ledger cell whose currency
 * the table states once: `1,250.00`, `-5.00`.
 */
export function formatAmount(amount: number | null | undefined, fallback = '—'): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return fallback
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/** `Sep 02, 2026 · 10:18 AM` — see `DISPLAY_DATE_TIME_MEDIUM_FORMAT`. */
export function formatDateTimeMedium(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(DISPLAY_DATE_TIME_MEDIUM_FORMAT) : fallback
}

/**
 * `Sep 03, 2026 · 9:12 AM` — the medium stamp with an unpadded hour, as the
 * Website Content designs write it (2026-10-14). Separate from the padded
 * `formatDateTimeMedium`, which reservations screens already use.
 */
export const DISPLAY_TIMESTAMP_FORMAT = 'MMM DD, YYYY · h:mm A'

export function formatTimestamp(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(DISPLAY_TIMESTAMP_FORMAT) : fallback
}

/** `Oct 22, 2026 at 07:01 AM` — a report's "Generated …" line (Reports design, 2026-10). */
export const GENERATED_AT_FORMAT = 'MMM DD, YYYY [at] hh:mm A'

export function formatGeneratedAt(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(GENERATED_AT_FORMAT) : fallback
}

/**
 * `Oct 21, 2026, 07:43 AM EST` — a report's footer stamp, with the viewer's
 * time-zone abbreviation from `Intl` (dayjs has no zone names without a plugin).
 */
export function formatGeneratedStamp(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  if (!d.isValid()) return fallback
  const zone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(d.toDate())
    .find((part) => part.type === 'timeZoneName')?.value
  const stamp = d.format('MMM DD, YYYY, hh:mm A')
  return zone ? `${stamp} ${zone}` : stamp
}

/** `10/31/2026` — the Financials Additionals ledger (2026-10 design). */
export const NUMERIC_DATE_FORMAT = 'MM/DD/YYYY'

export function formatNumericDate(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(NUMERIC_DATE_FORMAT) : fallback
}

/** `Aug 29, 2026`. */
export function formatDateMedium(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(RANGE_DATE_WITH_YEAR_FORMAT) : fallback
}

/** `Friday` — the Option Dates report's "Option date · Friday". */
export function formatWeekday(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format('dddd') : fallback
}

/**
 * A span of whole minutes in words: `1 minute`, `30 minutes`, `1 hour`,
 * `1 hour 30 minutes`, `24 hours`. Used where a server setting is a minute count.
 */
export function formatMinutesDuration(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes))
  const hours = Math.floor(whole / 60)
  const rest = whole % 60
  const part = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`
  if (hours === 0) return part(rest, 'minute')
  return rest === 0 ? part(hours, 'hour') : `${part(hours, 'hour')} ${part(rest, 'minute')}`
}

/** The time of day alone — a log table's second line under `formatDate`. */
export const DISPLAY_TIME_FORMAT = 'h:mm A'

export function formatTime(value: DateInput, fallback = '—'): string {
  if (!value) return fallback
  const d = dayjs(value)
  return d.isValid() ? d.format(DISPLAY_TIME_FORMAT) : fallback
}

/**
 * A start–end date range on one line, as the events listing design shows it:
 * `Sep 15 – Sep 17, 2026`. The year appears once, on the end date, unless the
 * range crosses a year boundary — then both carry it (`Dec 30, 2026 – Jan 02,
 * 2027`) so the span is unambiguous.
 *
 * Uses an en dash, matching the design and `Pagination`'s "1–10 of 42".
 */
export function formatDateRange(start: DateInput, end: DateInput, fallback = '—'): string {
  const from = dayjs(start)
  const to = dayjs(end)

  // A single valid end falls back to that one date rather than rendering a
  // half-open range the reader cannot interpret.
  if (!start || !from.isValid()) return formatDate(end, fallback)
  if (!end || !to.isValid()) return from.format(RANGE_DATE_WITH_YEAR_FORMAT)

  return from.isSame(to, 'year')
    ? `${from.format(RANGE_DATE_FORMAT)} – ${to.format(RANGE_DATE_WITH_YEAR_FORMAT)}`
    : `${from.format(RANGE_DATE_WITH_YEAR_FORMAT)} – ${to.format(RANGE_DATE_WITH_YEAR_FORMAT)}`
}

/**
 * A stay or event span written day-first and as short as it stays clear, as
 * the Secure Hotel Portal design draws it: `11 – 16 Mar 2027` within a month,
 * `28 Feb – 3 Mar 2027` within a year, both dates in full otherwise.
 */
export function formatDayRange(start: DateInput, end: DateInput, fallback = '—'): string {
  const from = dayjs(start)
  const to = dayjs(end)
  if (!start || !from.isValid()) return end && to.isValid() ? to.format('D MMM YYYY') : fallback
  if (!end || !to.isValid()) return from.format('D MMM YYYY')

  if (from.isSame(to, 'month')) return `${from.format('D')} – ${to.format('D MMM YYYY')}`
  if (from.isSame(to, 'year')) return `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`
  return `${from.format('D MMM YYYY')} – ${to.format('D MMM YYYY')}`
}

/** Serialises a date for the API. */
export function toApiDate(value: DateInput): string | null {
  if (!value) return null
  const d = dayjs(value)
  return d.isValid() ? d.format(API_DATE_FORMAT) : null
}

/** Nights between arrival and departure — the unit rooms and rates are sold in. */
export function nightsBetween(arrival: DateInput, departure: DateInput): number {
  if (!arrival || !departure) return 0
  const from = dayjs(arrival).startOf('day')
  const to = dayjs(departure).startOf('day')
  if (!from.isValid() || !to.isValid()) return 0
  return Math.max(0, to.diff(from, 'day'))
}

/** `value` moved by `days` (negative goes back), in the API format; `undefined` when unset. */
export function shiftApiDate(value: DateInput, days: number): string | undefined {
  if (!value) return undefined
  const d = dayjs(value)
  return d.isValid() ? d.add(days, 'day').format(API_DATE_FORMAT) : undefined
}

/** Every date in [from, to) — one entry per room-night. */
export function eachNight(from: DateInput, to: DateInput): string[] {
  const nights = nightsBetween(from, to)
  const start = dayjs(from).startOf('day')
  return Array.from({ length: nights }, (_, i) => start.add(i, 'day').format(API_DATE_FORMAT))
}

/** Whole days from today until `value`. Negative when in the past. */
export function daysUntil(value: DateInput): number {
  if (!value) return Number.NaN
  return dayjs(value).startOf('day').diff(dayjs().startOf('day'), 'day')
}

export function isPast(value: DateInput): boolean {
  if (!value) return false
  return dayjs(value).startOf('day').isBefore(dayjs().startOf('day'))
}

export function formatCurrency(
  amount: number | null | undefined,
  currency = 'USD',
  fallback = '—',
  /** `code` renders "USD 0.50" instead of "$0.50". */
  display: 'symbol' | 'code' = 'symbol',
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return fallback
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: display,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * The symbol a currency prints with (`$`, `€`, `CHF`) — for a money input's
 * leading adornment, where the value itself is typed unformatted.
 */
export function currencySymbol(currency = 'USD'): string {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).formatToParts(0)
    return parts.find((part) => part.type === 'currency')?.value ?? currency
  } catch {
    // Not an ISO 4217 code `Intl` knows — show the code itself rather than throw.
    return currency
  }
}

export function formatNumber(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback
  return new Intl.NumberFormat('en-US').format(value)
}

export function formatPercent(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)}%`
}

/**
 * The file name at the end of a storage key or URL — `public/event/x/photo.jpg`
 * and `https://cdn/…/photo.jpg?Expires=…` both read as `photo.jpg`. A bare
 * name comes back unchanged.
 */
export function fileNameOf(value: string): string {
  const path = value.split(/[?#]/)[0] ?? ''
  return path.slice(path.lastIndexOf('/') + 1) || value
}

/**
 * A file size as people read it: `486 KB` below a megabyte, `8.2 MB` from one
 * up (1 KB = 1024 bytes, as the upload limits are written).
 */
export function formatFileSize(bytes: number | null | undefined, fallback = '—'): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return fallback
  const megabytes = bytes / (1024 * 1024)
  if (megabytes >= 1) return `${Number(megabytes.toFixed(1))} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

export function fullName(first?: string | null, last?: string | null, fallback = '—'): string {
  const name = [first, last].filter(Boolean).join(' ').trim()
  return name || fallback
}

/**
 * Avatar fallback when a user has no photo. Takes a whole name — the API sends
 * `fullName` as one string — and uses its first and last word, so "Turon Travel"
 * gives "TT" and a single word gives one letter.
 */
export function initials(name?: string | null, fallback = '?'): string {
  const words = name?.trim().split(/\s+/).filter(Boolean) ?? []
  if (words.length === 0) return fallback

  const first = words[0]?.charAt(0) ?? ''
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : ''
  return `${first}${last}`.toUpperCase() || fallback
}

/**
 * Sentence case for a status label the API sends in its own casing
 * ("InActive", "Published Online" → "Inactive", "Published online") — status
 * labels are sentence case portal-wide (client, 2026-10-28). Only for plain
 * status words: it lowercases everything after the first letter, acronyms too.
 */
export function toSentenceCase(value: string): string {
  const trimmed = value.trim()
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

export { dayjs }
